import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface InitialPaymentBreakdown {
  /** Total charged to client (from client_charge entry, absolute value) */
  totalChargedCents: number;
  /** Base amount (from escrow_hold entry - excludes tax and fees) */
  subtotalCents: number;
  /** Tax collected (from tax_collected entry) */
  taxCents: number;
  /** CC processing fee (from cc_fee_collected entry) */
  ccFeeCents: number;
  /** Whether this data came from ledger (true) or fallback calculation (false) */
  isFromLedger: boolean;
}

/**
 * Hook to fetch the initial payment breakdown from the financial ledger.
 * This provides consistent financial data across ALL payment types.
 * 
 * The ledger is the single source of truth for payment breakdowns:
 * - client_charge: Total amount charged to client
 * - escrow_hold: Base PO amount (subtotal)
 * - tax_collected: Sales tax
 * - cc_fee_collected: Credit card processing fee
 */
export function useInitialPaymentLedger(jobId: string | null, purchaseOrderId: string | null) {
  return useQuery({
    queryKey: ['initial-payment-ledger', jobId, purchaseOrderId],
    queryFn: async (): Promise<InitialPaymentBreakdown | null> => {
      if (!jobId) return null;

      // First, find the initial payment transaction to get its correlation_id
      const { data: initialPaymentTx } = await supabase
        .from('payment_transactions')
        .select('id')
        .eq('job_id', jobId)
        .eq('transaction_type', 'initial_payment')
        .is('adjustment_id', null)
        .maybeSingle();

      if (!initialPaymentTx) {
        // No initial payment found - this might be a very old PO or one without payment yet
        return null;
      }

      // Fetch ledger entries for this initial payment
      // The ledger entries are linked via source_transaction_id
      const { data: ledgerEntries, error } = await supabase
        .from('financial_ledger')
        .select('entry_type, amount_cents')
        .eq('job_id', jobId)
        .eq('source_transaction_id', initialPaymentTx.id)
        .eq('status', 'confirmed');

      if (error) {
        console.error('Error fetching ledger entries:', error);
        return null;
      }

      if (!ledgerEntries || ledgerEntries.length === 0) {
        // Fallback: No ledger entries yet - try to get data from payment_transactions directly
        const { data: txData } = await supabase
          .from('payment_transactions')
          .select('total_amount_cents, tax_amount_cents, vendor_tax_cents, cc_processing_fee_cents')
          .eq('id', initialPaymentTx.id)
          .single();

        if (txData) {
          // Calculate subtotal from transaction data
          const taxCents = txData.tax_amount_cents || txData.vendor_tax_cents || 0;
          const ccFeeCents = txData.cc_processing_fee_cents || 0;
          const totalChargedCents = txData.total_amount_cents;
          // Subtotal = total - tax - cc fee
          const subtotalCents = totalChargedCents - ccFeeCents;
          // Note: For invoice payments, total_amount_cents already includes tax
          // but doesn't include CC fees (there are none)

          return {
            totalChargedCents,
            subtotalCents: subtotalCents - taxCents, // Remove tax to get pure subtotal
            taxCents,
            ccFeeCents,
            isFromLedger: false,
          };
        }
        return null;
      }

      // Parse ledger entries to build breakdown
      let totalChargedCents = 0;
      let subtotalCents = 0;
      let taxCents = 0;
      let ccFeeCents = 0;

      for (const entry of ledgerEntries) {
        switch (entry.entry_type) {
          case 'client_charge':
            // Client charge is negative (debit from client), we want the absolute value
            totalChargedCents = Math.abs(entry.amount_cents);
            break;
          case 'escrow_hold':
            // Escrow hold is positive - this is the base PO amount
            subtotalCents = entry.amount_cents;
            break;
          case 'tax_collected':
            // Tax collected is positive
            taxCents = entry.amount_cents;
            break;
          case 'cc_fee_collected':
            // CC fee collected is positive
            ccFeeCents = entry.amount_cents;
            break;
        }
      }

      return {
        totalChargedCents,
        subtotalCents,
        taxCents,
        ccFeeCents,
        isFromLedger: true,
      };
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
}
